import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { ActivityType, ActivityDirection, ActivityOutcome } from "@crm/shared/types";

const KEY = "activities";

export function useActivityList(options?: {
	companyId?: string | undefined;
	opportunityId?: string | undefined;
	type?: string | undefined;
	search?: string | undefined;
	createdAtFrom?: string | undefined;
	createdAtTo?: string | undefined;
	isCompleted?: boolean | undefined;
	personId?: string | undefined;
	dueDateFrom?: string | undefined;
	dueDateTo?: string | undefined;
	ownerId?: string | undefined;
	direction?: string | undefined;
	outcome?: string | undefined;
	page?: number;
	limit?: number;
	enabled?: boolean;
}) {
	const page = options?.page ?? 1;
	const limit = options?.limit ?? 20;
	const offset = (page - 1) * limit;
	return useQuery({
		queryKey: [KEY, "list", options],
		queryFn: () => {
			const params = new URLSearchParams();
			params.set("limit", String(limit));
			params.set("offset", String(offset));
			if (options?.companyId) params.set("companyId", options.companyId);
			if (options?.opportunityId) params.set("opportunityId", options.opportunityId);
			if (options?.type) params.set("type", options.type);
			if (options?.search) params.set("search", options.search);
			if (options?.createdAtFrom) params.set("createdAtFrom", options.createdAtFrom);
			if (options?.createdAtTo) params.set("createdAtTo", options.createdAtTo);
			if (options?.isCompleted !== undefined)
				params.set("isCompleted", String(options.isCompleted));
			if (options?.personId) params.set("personId", options.personId);
			if (options?.dueDateFrom) params.set("dueDateFrom", options.dueDateFrom);
			if (options?.dueDateTo) params.set("dueDateTo", options.dueDateTo);
			if (options?.ownerId) params.set("ownerId", options.ownerId);
			if (options?.direction) params.set("direction", options.direction);
			if (options?.outcome) params.set("outcome", options.outcome);
			return api.get<{ items: Activity[]; total: number }>(`/api/activities?${params.toString()}`);
		},
		enabled: options?.enabled !== false,
	});
}

export function useActivityDetail(id: string) {
	return useQuery({
		queryKey: [KEY, "detail", id],
		queryFn: () => api.get<Activity>(`/api/activities/${id}`),
		enabled: !!id,
	});
}

export function useCreateActivity() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateActivityInput) => api.post<Activity>("/api/activities", data),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
		},
	});
}

export function useUpdateActivity() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<CreateActivityInput> }) =>
			api.patch<Activity>(`/api/activities/${id}`, data),
		onSuccess: (_, vars) => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: [KEY, "detail", vars.id] });
		},
	});
}

export function useDeleteActivity() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.delete<void>(`/api/activities/${id}`),
		onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, "list"] }),
	});
}

interface Activity {
	id: string;
	type: ActivityType;
	subject: string;
	content: string | null;
	dueDate: string | null;
	completedDate: string | null;
	isCompleted: boolean;
	companyId: string | null;
	personId: string | null;
	opportunityId: string | null;
	assignedTo: string | null;
	createdBy: string | null;
	contactName: string | null;
	contactOrgName: string | null;
	contactPhone: string | null;
	contactEmail: string | null;
	location: string | null;
	direction: ActivityDirection | null;
	outcome: ActivityOutcome | null;
	ownerName?: string | null;
	creatorName?: string | null;
	companyName?: string | null;
	personName?: string | null;
	createdAt: string;
	updatedAt: string;
}

interface CreateActivityInput {
	type: Activity["type"];
	subject: string;
	content?: string | undefined;
	dueDate?: string | undefined;
	companyId?: string | undefined;
	personId?: string | undefined;
	opportunityId?: string | undefined;
	assignedTo?: string | undefined;
	isCompleted?: boolean | undefined;
	completedDate?: string | null | undefined;
	contactName?: string | undefined;
	contactOrgName?: string | undefined;
	contactPhone?: string | undefined;
	contactEmail?: string | undefined;
	location?: string | undefined;
	direction?: ActivityDirection | undefined;
	outcome?: ActivityOutcome | undefined;
}
